import { Popconfirm } from 'antd';
import _ from 'lodash';
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import {
  setTableColumnSelection,
  clearSlim360O365UserCopilotActivitySummaryMessages,
  slim360O365UserCopilotActivitySummarySelector,
} from '../../../../store/slim360/o365UserCopilotActivitySummary/o365UserCopilotActivitySummary.reducer';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import {
  deleteSlim360O365UserCopilotActivitySummary,
  searchSlim360O365UserCopilotActivitySummary,
} from '../../../../store/slim360/o365UserCopilotActivitySummary/o365UserCopilotActivitySummary.action';
import slim360O365UserCopilotActivitySummaryService from '../../../../services/slim360/o365UserCopilotActivitySummary/o365UserCopilotActivitySummary.service';
import {
  FilterByDateSwap,
  FilterWithSwapOption,
} from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable, ISearch } from '../../../../common/models/common';
import { useHistory } from 'react-router-dom';
import DataTable from '../../../../common/components/DataTable';
import ability, { Can } from '../../../../common/ability';
import { Action, Page } from '../../../../common/constants/pageAction';
import { showDateFromApi } from '../../../../common/helperFunction';

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (props, ref) => {
  const {
    setSelectedId,
    setShowSelectedListModal,
    setValuesForSelection,
    isMultiple,
    tableButtons,
  } = props;
  const slim360O365UserCopilotActivitySummary = useAppSelector(
    slim360O365UserCopilotActivitySummarySelector
  );
  const dispatch = useAppDispatch();
  const dataTableRef = useRef(null);
  const history = useHistory();
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  useEffect(() => {
    if (isMultiple) {
      dataTableRef?.current.getValuesForSelection();
    }
  }, [isMultiple]);

  const exportExcelFile = (searchData: ISearch) => {
    return slim360O365UserCopilotActivitySummaryService.exportExcelFile(searchData);
  };

  const FilterBySwap = (dataIndex: string, form, isRelativeDropDownFlag?: boolean) => {
    return FilterWithSwapOption(
      dataIndex,
      slim360O365UserCopilotActivitySummary.search.tableName,
      form,
      null,
      ObjectForColumnFilter,
      null,
      null,
      isRelativeDropDownFlag
    );
  };

  const FilterByDateSwapTable = (dataIndex: string, tableName: string, form: any) => {
    return FilterByDateSwap(dataIndex, tableName, form, null, ObjectForColumnFilter);
  };

  const getTableColumns = (form) => {
    return [
      {
        title: <span className="dragHandler">ID</span>,
        column: 'id',
        sorter: true,
        ellipsis: true,
        children: [
          {
            title: FilterBySwap('id', form),
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Tenant Name</span>,
        column: 'TenantId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('tenant_id', form, true),
            dataIndex: 'tenant_name',
            key: 'tenant_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Company Name</span>,
        column: 'CompanyId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('company_id', form, true),
            dataIndex: 'company_name',
            key: 'company_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Bu Name</span>,
        column: 'BU_Id',
        sorter: true,
        children: [
          {
            title: FilterBySwap('bu_id', form, true),
            dataIndex: 'bu_name',
            key: 'bu_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Date Added</span>,
        column: 'Date Added',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'date_added',
              slim360O365UserCopilotActivitySummary.search.tableName,
              form
            ),
            dataIndex: 'date_added',
            key: 'date_added',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Azure Tenant Id</span>,
        column: 'azureTenantId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('azure_tenant_id', form),
            dataIndex: 'azure_tenant_id',
            key: 'azure_tenant_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">User Principal Name</span>,
        column: 'userPrincipalName',
        sorter: true,
        children: [
          {
            title: FilterBySwap('user_principal_name', form),
            dataIndex: 'user_principal_name',
            key: 'user_principal_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Report Period</span>,
        column: 'reportPeriod',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'report_period',
              slim360O365UserCopilotActivitySummary.search.tableName,
              form
            ),
            dataIndex: 'report_period',
            key: 'report_period',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Year</span>,
        column: 'year',
        sorter: true,
        children: [
          {
            title: FilterBySwap('year', form),
            dataIndex: 'year',
            key: 'year',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Week</span>,
        column: 'week',
        sorter: true,
        children: [
          {
            title: FilterBySwap('week', form),
            dataIndex: 'week',
            key: 'week',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Score</span>,
        column: 'score',
        sorter: true,
        children: [
          {
            title: FilterBySwap('score', form),
            dataIndex: 'score',
            key: 'score',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Weight</span>,
        column: 'weight',
        sorter: true,
        children: [
          {
            title: FilterBySwap('weight', form),
            dataIndex: 'weight',
            key: 'weight',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Score Teams</span>,
        column: 'scoreTeams',
        sorter: true,
        children: [
          {
            title: FilterBySwap('score_teams', form),
            dataIndex: 'score_teams',
            key: 'score_teams',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Weight Teams</span>,
        column: 'weightTeams',
        sorter: true,
        children: [
          {
            title: FilterBySwap('weight_teams', form),
            dataIndex: 'weight_teams',
            key: 'weight_teams',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">BizChat</span>,
        column: 'bizchat',
        sorter: true,
        children: [
          {
            title: FilterBySwap('bizchat', form),
            dataIndex: 'bizchat',
            key: 'bizchat',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Excel</span>,
        column: 'excel',
        sorter: true,
        children: [
          {
            title: FilterBySwap('excel', form),
            dataIndex: 'excel',
            key: 'excel',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Loop</span>,
        column: 'loop',
        sorter: true,
        children: [
          {
            title: FilterBySwap('loop', form),
            dataIndex: 'loop',
            key: 'loop',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Other</span>,
        column: 'other',
        sorter: true,
        children: [
          {
            title: FilterBySwap('other', form),
            dataIndex: 'other',
            key: 'other',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Outlook</span>,
        column: 'outlook',
        sorter: true,
        children: [
          {
            title: FilterBySwap('outlook', form),
            dataIndex: 'outlook',
            key: 'outlook',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">PowerPoint</span>,
        column: 'powerpoint',
        sorter: true,
        children: [
          {
            title: FilterBySwap('powerpoint', form),
            dataIndex: 'powerpoint',
            key: 'powerpoint',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Teams Chat</span>,
        column: 'teamsChat',
        sorter: true,
        children: [
          {
            title: FilterBySwap('teams_chat', form),
            dataIndex: 'teams_chat',
            key: 'teams_chat',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Teams Meeting</span>,
        column: 'teamsMeeting',
        sorter: true,
        children: [
          {
            title: FilterBySwap('teams_meeting', form),
            dataIndex: 'teams_meeting',
            key: 'teams_meeting',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Whiteboard</span>,
        column: 'whiteboard',
        sorter: true,
        children: [
          {
            title: FilterBySwap('whiteboard', form),
            dataIndex: 'whiteboard',
            key: 'whiteboard',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Word</span>,
        column: 'word',
        sorter: true,
        children: [
          {
            title: FilterBySwap('word', form),
            dataIndex: 'word',
            key: 'word',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Calculated Date</span>,
        column: 'lastCalculatedDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'last_calculated_date',
              slim360O365UserCopilotActivitySummary.search.tableName,
              form
            ),
            dataIndex: 'last_calculated_date',
            key: 'last_calculated_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Modified Date</span>,
        column: 'lastModifiedDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'last_modified_date',
              slim360O365UserCopilotActivitySummary.search.tableName,
              form
            ),
            dataIndex: 'last_modified_date',
            key: 'last_modified_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">SLIM360 Id</span>,
        column: 'slimId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('slim_id', form),
            dataIndex: 'slim_id',
            key: 'slim_id',
            ellipsis: true,
          },
        ],
      },
    ];
  };

  const removeSlim360O365UserCopilotActivitySummary = (id: number) => {
    dispatch(deleteSlim360O365UserCopilotActivitySummary(id));
  };
  const tableAction = (_, data: any) => (
    <div className="btns-block">
      <Can I={Action.Update} a={Page.Slim360O365UserCopilotActivitySummary}>
        <a
          className="action-btn"
          onClick={() => {
            setSelectedId(data.id);
            history.push(`/slim360/slim360-o365-user-copilot-activity-summary/${data.id}`);
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/images/ic-edit.svg`} alt="" />
        </a>
      </Can>
      <Can I={Action.Delete} a={Page.Slim360O365UserCopilotActivitySummary}>
        <Popconfirm
          title="Delete Record?"
          onConfirm={() => removeSlim360O365UserCopilotActivitySummary(data.id)}
        >
          <a href="#" title="" className="action-btn">
            <img src={`${process.env.PUBLIC_URL}/assets/images/ic-delete.svg`} alt="" />
          </a>
        </Popconfirm>
      </Can>
    </div>
  );

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={ability.can(Action.Add, Page.Slim360O365UserCopilotActivitySummary)}
        setSelectedId={setSelectedId}
        tableAction={tableAction}
        exportExcelFile={exportExcelFile}
        getTableColumns={getTableColumns}
        reduxSelector={slim360O365UserCopilotActivitySummarySelector}
        searchTableData={searchSlim360O365UserCopilotActivitySummary}
        clearTableDataMessages={clearSlim360O365UserCopilotActivitySummaryMessages}
        setTableColumnSelection={setTableColumnSelection}
        setShowSelectedListModal={setShowSelectedListModal}
        setValuesForSelection={setValuesForSelection}
        showBulkUpdate={ability.can(Action.Update, Page.Slim360O365UserCopilotActivitySummary)}
        setObjectForColumnFilter={setObjectForColumnFilter}
        tableButtons={tableButtons}
      />
    </>
  );
};

export default forwardRef(MainTable);
